/**
 * TimeRangeSelector Component
 * Reusable time range dropdown for analytics dashboards
 *
 * @description Shared component for selecting time ranges (7d, 30d, 90d, all time)
 */

import { Calendar } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";

/**
 * Standard time range options for analytics
 */
export type TimeRange = "7d" | "30d" | "90d" | "all";

/**
 * Time range labels for display
 */
export const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  all: "All time",
};

/**
 * Get the start date for a given time range
 */
export function getTimeRangeStartDate(range: TimeRange): Date | null {
  const now = new Date();
  switch (range) {
    case "7d":
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case "30d":
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    case "90d":
      return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    case "all":
      return null; // No start date filter
  }
}

interface TimeRangeSelectorProps {
  readonly value: TimeRange;
  readonly onChange: (value: TimeRange) => void;
  readonly className?: string;
  readonly showIcon?: boolean;
}

export function TimeRangeSelector({ value, onChange, className = "", showIcon = true }: TimeRangeSelectorProps) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TimeRange)}>
      <SelectTrigger
        className={`w-[180px] bg-neutral-900/50 border-neutral-800 text-white hover:bg-neutral-900 ${className}`}
      >
        <div className="flex items-center gap-2">
          {showIcon && <Calendar className="h-4 w-4 text-neutral-400" />}
          <span>{TIME_RANGE_LABELS[value]}</span>
        </div>
      </SelectTrigger>
      <SelectContent className="bg-neutral-900 border-neutral-800">
        {(Object.keys(TIME_RANGE_LABELS) as TimeRange[]).map((range) => (
          <SelectItem key={range} value={range} className="text-white hover:bg-neutral-800 focus:bg-neutral-800">
            {TIME_RANGE_LABELS[range]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
